/**
 * Los movimientos del período: qué entró, qué salió, de qué caja.
 *
 * Es una tabla densa a proposito. Mas filas visibles es menos scroll, y menos
 * scroll es menos chance de leer mal una fila por la de al lado. En mobile se
 * convierte en lista de tarjetas: una tabla de cinco columnas con scroll
 * horizontal en un celular no se lee, se adivina.
 *
 * EL PIE NO MIENTE
 * ----------------
 * Los totales vienen del servidor y son del set filtrado COMPLETO, no de las
 * filas que entraron en la pagina. Y dejan afuera las transferencias entre
 * cuentas propias, que se cuentan aparte: mover plata de la caja al banco es un
 * egreso y un ingreso por el mismo importe, y sumarlos infla las dos columnas
 * hasta dar un mes de mucho movimiento donde no entro ni salio nada.
 */
import { useMemo, useState, type ReactNode } from "react";
import { ArrowDownLeft, ArrowUpRight, ChevronRight } from "lucide-react";

import { Importe } from "@/components/money";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { Label } from "@/components/ui/label";
import { Segmented } from "@/components/ui/segmented";
import { SelectNativo } from "@/components/ui/select-nativo";
import { SelectorPeriodo } from "@/components/ui/selector-periodo";
import { SkeletonTabla, SkeletonTarjetas } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import type { Periodo } from "@/lib/periodo";
import { cn } from "@/lib/utils";
import { formatearFechaHora } from "@/lib/utils";
import {
  aNumero,
  type CuentaFinanciera,
  type MovimientoCaja,
  type TotalMovimientos,
  type TotalesMovimientos,
} from "@/lib/tipos";

import { ETIQUETA_CONCEPTO, nombreCuenta, sePuedeAnular } from "./logica";

const COLUMNAS = 7;
const MENSAJE_VACIO = "No hay movimientos con estos filtros. Probá con otro mes o con otra cuenta.";

export interface FiltrosVisibles {
  /** Lo que se escribe en la lupa: detalle, observacion, concepto o importe. */
  texto: string;
  cuentaId: string;
  tipo: string;
  periodo: Periodo;
}

export interface MovimientosProps {
  movimientos: ReadonlyArray<MovimientoCaja>;
  cuentas: ReadonlyArray<CuentaFinanciera>;
  filtros: FiltrosVisibles;
  onFiltros: (f: FiltrosVisibles) => void;
  /** Del servidor, sobre todo lo filtrado. Sin esto el pie no se muestra. */
  totales?: TotalesMovimientos;
  cargando?: boolean;
  /**
   * El `PieDeLista` de quien usa la lista: cuantos movimientos se trajeron de
   * cuantos hay en el periodo. Va adentro del cuerpo con scroll porque la
   * pantalla es de alto fijo y un bloque mas abajo le comeria el alto.
   */
  pie?: ReactNode;
  /** Para decir de quien es un retiro, un aporte o un sueldo de socio. */
  socios?: ReadonlyArray<{ id: number; nombre: string }>;
  /**
   * Anular un movimiento cargado a mano (0155). Sin esto no se ofrece. Lo que
   * genero una venta, un cobro o un pago se deshace desde ahi.
   */
  onAnular?: (m: MovimientoCaja) => void;
  /** En el celular, con la pestaña Saldos elegida, la lista no se muestra. */
  ocultoEnCelular?: boolean;
}

/**
 * El detalle de un renglon, con el socio adelante cuando es plata de un socio.
 * Un retiro sin detalle decia «RETIRO DE SOCIO —», sin saber de quien: la
 * pantalla Socios los distinguia y Caja no.
 */
export function detalleMovimiento(
  m: Pick<MovimientoCaja, "detalle" | "socio_id" | "revision_nota">,
  socios: ReadonlyArray<{ id: number; nombre: string }>,
): string | null {
  const socio = m.socio_id != null ? socios.find((s) => s.id === Number(m.socio_id)) : undefined;
  // (0156) Lo que explico quien lo reviso queda a la vista.
  const revision = m.revision_nota ? `Revisión: ${m.revision_nota}` : null;
  const partes = [socio?.nombre, m.detalle, revision].filter(Boolean);
  return partes.length > 0 ? partes.join(" · ") : null;
}

export function Movimientos({
  movimientos,
  cuentas,
  filtros,
  onFiltros,
  totales,
  cargando = false,
  pie,
  socios = [],
  onAnular,
  ocultoEnCelular = false,
}: MovimientosProps): JSX.Element {
  const vacio = movimientos.length === 0;
  const busqueda = filtros.texto.trim();
  const mensajeVacio = busqueda
    ? `No encontré movimientos con «${busqueda}» en este período. Probá con otro mes o con menos letras.`
    : MENSAJE_VACIO;
  // Skeleton solo en la primera carga (06 §4.2); con datos en pantalla, un
  // cambio de filtro atenua la lista y nunca la vacia.
  const primeraCarga = cargando && vacio;
  const actualizando = cargando && !vacio;

  return (
    <>
      {/*
        Los controles en UN renglon. Si esto se parte en dos, el alto que
        gana la pantalla fija se lo come la barra de filtros.
      */}
      <div
        className={cn(
          "flex flex-wrap items-center gap-x-4 gap-y-2",
          ocultoEnCelular && "max-md:hidden",
        )}
      >
        <InputBusqueda
          valor={filtros.texto}
          onValorChange={(texto) => onFiltros({ ...filtros, texto })}
          etiqueta="Buscar movimiento por detalle, concepto o importe"
          placeholder="Buscar movimiento"
          className="min-w-48 max-w-xs flex-1"
        />
        <div className="flex items-center gap-2">
          <TituloSeccion nivel="etiqueta" as="span">
            Tipo
          </TituloSeccion>
          <Segmented
            etiqueta="Tipo de movimiento"
            apariencia="chips"
            tamano="sm"
            opciones={[
              { valor: "", etiqueta: "Todo" },
              { valor: "INGRESO", etiqueta: "Entradas" },
              { valor: "EGRESO", etiqueta: "Salidas" },
            ]}
            valor={filtros.tipo}
            onChange={(v) => onFiltros({ ...filtros, tipo: v })}
          />
        </div>

        {/*
          Desplegable y no chips: con cuatro cuentas los chips entran, con ocho
          parten la barra en dos renglones. Nativo y no el `Select` de Radix
          porque son pocas opciones, sin busqueda, y en el celular abre el
          picker del sistema.
        */}
        <div className="flex items-center gap-2">
          <Label htmlFor="caja-cuenta" className="shrink-0">
            Cuenta
          </Label>
          <SelectNativo
            id="caja-cuenta"
            className="w-48"
            value={filtros.cuentaId}
            onChange={(e) => onFiltros({ ...filtros, cuentaId: e.target.value })}
          >
            <option value="">Todas las cuentas</option>
            {cuentas.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.nombre} ({c.moneda})
              </option>
            ))}
          </SelectNativo>
        </div>

        <SelectorPeriodo
          valor={filtros.periodo}
          onChange={(periodo) => onFiltros({ ...filtros, periodo })}
          className="ml-auto"
        />
      </div>

      <section
        aria-busy={actualizando || undefined}
        className={cn(
          "flex min-h-0 flex-1 flex-col transition-opacity duration-150",
          actualizando && "opacity-60",
          ocultoEnCelular && "max-md:hidden",
        )}
      >
        {/* Desktop: tabla densa, con el scroll adentro. */}
        <Card padding="none" className="hidden min-h-0 flex-1 flex-col overflow-hidden md:flex">
          {primeraCarga ? (
            <SkeletonTabla columnas={COLUMNAS} densidad="compacta" />
          ) : (
            <Table densidad="compacta" alto="lleno">
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Cuenta</TableHead>
                  <TableHead>Concepto</TableHead>
                  <TableHead>Detalle</TableHead>
                  <TableHead>Cargó</TableHead>
                  <TableHead className="text-right">Importe</TableHead>
                  <TableHead>
                    <span className="sr-only">Acciones</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vacio ? (
                  <TableVacia colSpan={COLUMNAS} mensaje={mensajeVacio} />
                ) : (
                  movimientos.map((m) => (
                    <TableRow key={String(m.id)}>
                      <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                        {formatearFechaHora(m.fecha)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {nombreCuenta(cuentas, m.cuenta_id)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <EtiquetaConcepto movimiento={m} />
                      </TableCell>
                      <TableCell className="max-w-[12rem] truncate text-sm text-muted-foreground xl:max-w-[20rem]">
                        {detalleMovimiento(m, socios) ?? "—"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {m.usuario_nombre ?? "—"}
                      </TableCell>
                      <TableCellImporte>
                        <ImporteFirmado movimiento={m} />
                      </TableCellImporte>
                      <TableCell className="w-0 whitespace-nowrap text-right">
                        {onAnular && sePuedeAnular(m) ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => onAnular(m)}
                            aria-label={`Anular ${ETIQUETA_CONCEPTO[m.concepto] ?? m.concepto} de ${formatearFechaHora(m.fecha)}`}
                          >
                            Anular
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {!vacio && pie ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={COLUMNAS}>{pie}</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
              {!vacio && totales ? <PieDelPeriodo totales={totales} /> : null}
            </Table>
          )}
        </Card>

        {/*
          Mobile: los movimientos agrupados por día, con lo que entró y salió
          arriba de cada día, en renglones de dos líneas que se tocan para ver
          el detalle. Antes eran tarjetas con la fecha con año y el detalle en
          un solo renglón cortado: «Pago a AyR Tecno» se leía «P…», y el total
          del período estaba al pie, a siete deslizamientos.
        */}
        <div className="md:hidden">
          {primeraCarga ? (
            <SkeletonTarjetas />
          ) : vacio ? (
            <EstadoVacio titulo={mensajeVacio} />
          ) : (
            <>
              <ListaPorDia
                movimientos={movimientos}
                cuentas={cuentas}
                socios={socios}
                onAnular={onAnular}
              />
              {totales ? <ResumenMobile totales={totales} /> : null}
              {pie}
            </>
          )}
        </div>
      </section>
    </>
  );
}

/** «Miércoles 04/11». Local: el día es el del local, no el de UTC. */
function etiquetaDia(fecha: string): string {
  const d = new Date(fecha);
  const dia = d.toLocaleDateString("es-AR", { weekday: "long" });
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dia.charAt(0).toUpperCase()}${dia.slice(1)} ${dd}/${mm}`;
}

function claveDia(fecha: string): string {
  const d = new Date(fecha);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function hora(fecha: string): string {
  return new Date(fecha).toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * Mover plata entre cajas propias (y comprar dólares con pesos propios) no es
 * entrada ni salida del negocio: no suma en el resumen del día, igual que en
 * el pie del período.
 */
function esTraspaso(m: MovimientoCaja): boolean {
  return m.concepto === "TRANSFERENCIA" || m.concepto === "CAMBIO_DIVISA";
}

interface Dia {
  clave: string;
  etiqueta: string;
  movimientos: MovimientoCaja[];
  entro: { ARS: number; USD: number };
  salio: { ARS: number; USD: number };
}

export function agruparPorDia(movimientos: ReadonlyArray<MovimientoCaja>): Dia[] {
  const dias: Dia[] = [];
  for (const m of movimientos) {
    const clave = claveDia(m.fecha);
    let dia = dias[dias.length - 1];
    if (!dia || dia.clave !== clave) {
      dia = {
        clave,
        etiqueta: etiquetaDia(m.fecha),
        movimientos: [],
        entro: { ARS: 0, USD: 0 },
        salio: { ARS: 0, USD: 0 },
      };
      dias.push(dia);
    }
    dia.movimientos.push(m);
    if (!esTraspaso(m)) {
      const monto = aNumero(m.importe);
      if (m.tipo === "INGRESO") dia.entro[m.moneda] += monto;
      else dia.salio[m.moneda] += monto;
    }
  }
  return dias;
}

function MontosDelDia({ montos }: { montos: { ARS: number; USD: number } }): JSX.Element {
  const hay = (["ARS", "USD"] as const).filter((mon) => montos[mon] > 0);
  if (hay.length === 0) return <Importe monto={0} moneda="ARS" tamano="sm" codigo={false} />;
  return (
    <>
      {hay.map((mon, i) => (
        <span key={mon}>
          {i > 0 ? " y " : ""}
          <Importe monto={montos[mon]} moneda={mon} tamano="sm" codigo={false} />
        </span>
      ))}
    </>
  );
}

function ListaPorDia({
  movimientos,
  cuentas,
  socios,
  onAnular,
}: {
  movimientos: ReadonlyArray<MovimientoCaja>;
  cuentas: ReadonlyArray<CuentaFinanciera>;
  socios: ReadonlyArray<{ id: number; nombre: string }>;
  onAnular?: ((m: MovimientoCaja) => void) | undefined;
}): JSX.Element {
  const dias = useMemo(() => agruparPorDia(movimientos), [movimientos]);
  const [abierto, setAbierto] = useState<MovimientoCaja | null>(null);

  return (
    // `overflow-clip` y no `overflow-hidden`: recorta igual las esquinas, pero
    // no crea una zona de deslizamiento propia, así el título de cada día
    // queda pegado arriba mientras se desliza.
    <Card padding="none" className="overflow-clip">
      <p className="border-b px-4 py-2 text-xs text-muted-foreground">
        Lo que entró y salió de cada día deja afuera lo que se movió entre cajas propias.
      </p>
      {dias.map((dia) => (
        <section key={dia.clave} aria-label={dia.etiqueta}>
          {/* `-top-4`: pegado al borde de arriba y no a 16 px (el margen de
              `<main>`), por donde asomaba la lista. */}
          <header className="sticky -top-4 z-10 flex flex-col gap-0.5 border-b bg-muted px-4 py-2">
            <h3 className="text-sm font-semibold">{dia.etiqueta}</h3>
            <p className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
              <span>
                Entró <MontosDelDia montos={dia.entro} />
              </span>
              <span>
                Salió <MontosDelDia montos={dia.salio} />
              </span>
            </p>
          </header>
          <ul>
            {dia.movimientos.map((m) => {
              const detalle = detalleMovimiento(m, socios);
              const concepto = ETIQUETA_CONCEPTO[m.concepto] ?? m.concepto;
              return (
                <li key={String(m.id)} className="border-b last:border-b-0">
                  {/* `h-auto sm:h-auto` y `whitespace-normal`: el botón trae alto
                      fijo y una línea, y acá el detalle tiene que poder bajar. */}
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setAbierto(m)}
                    // eslint-disable-next-line tora/pendiente -- renglón de lista de dos líneas: necesita alto automático, no un tamaño de botón
                    className="h-auto min-h-14 w-full justify-start gap-3 whitespace-normal rounded-none px-4 py-2 text-left font-normal sm:h-auto"
                  >
                    <span className="w-10 shrink-0 text-xs tabular-nums text-muted-foreground">
                      {hora(m.fecha)}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="break-words text-sm font-medium">{detalle ?? concepto}</span>
                      <span className="text-xs text-muted-foreground">
                        {detalle ? `${concepto} · ` : ""}
                        {nombreCuenta(cuentas, m.cuenta_id)}
                      </span>
                    </span>
                    <span className="shrink-0">
                      <ImporteFirmado movimiento={m} compacto />
                    </span>
                    <ChevronRight
                      className="size-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </Button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      <DetalleMovimiento
        movimiento={abierto}
        cuentas={cuentas}
        socios={socios}
        onCerrar={() => setAbierto(null)}
        onAnular={onAnular}
      />
    </Card>
  );
}

/** Todo lo que se sabe de un movimiento, al tocarlo en el celular. */
function DetalleMovimiento({
  movimiento: m,
  cuentas,
  socios,
  onCerrar,
  onAnular,
}: {
  movimiento: MovimientoCaja | null;
  cuentas: ReadonlyArray<CuentaFinanciera>;
  socios: ReadonlyArray<{ id: number; nombre: string }>;
  onCerrar: () => void;
  /** Anular lo cargado a mano (0155): en el celular se ofrece desde el detalle. */
  onAnular?: ((m: MovimientoCaja) => void) | undefined;
}): JSX.Element {
  const filas: Array<[string, ReactNode]> = m
    ? [
        ["Fecha", formatearFechaHora(m.fecha)],
        ["Caja", nombreCuenta(cuentas, m.cuenta_id)],
        ["Concepto", ETIQUETA_CONCEPTO[m.concepto] ?? m.concepto],
        ["Detalle", detalleMovimiento(m, socios) ?? "—"],
        ...(m.usuario_nombre
          ? ([["Lo cargó", m.usuario_nombre]] as Array<[string, ReactNode]>)
          : []),
        ...(m.observaciones
          ? ([["Observaciones", m.observaciones]] as Array<[string, ReactNode]>)
          : []),
        ...(m.tarjeta_marca
          ? ([
              [
                "Tarjeta",
                `${m.tarjeta_marca}${m.cuotas ? ` · ${m.cuotas} cuotas` : ""}${m.arancel_pct ? ` · arancel ${aNumero(m.arancel_pct)}%` : ""}`,
              ],
            ] as Array<[string, ReactNode]>)
          : []),
      ]
    : [];

  return (
    <Dialog open={m !== null} onOpenChange={(a) => !a && onCerrar()}>
      <DialogContent tamano="sm">
        <DialogHeader>
          <DialogTitle>{m?.tipo === "INGRESO" ? "Entrada" : "Salida"}</DialogTitle>
          <DialogDescription className="text-2xl font-semibold text-foreground">
            {m ? <ImporteFirmado movimiento={m} /> : null}
          </DialogDescription>
        </DialogHeader>
        <dl className="flex flex-col gap-3">
          {filas.map(([etiqueta, valor]) => (
            <div key={etiqueta} className="flex flex-col gap-0.5">
              <dt className="text-xs text-muted-foreground">{etiqueta}</dt>
              <dd className="break-words text-sm">{valor}</dd>
            </div>
          ))}
        </dl>
        {m && onAnular && sePuedeAnular(m) ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onCerrar();
              onAnular(m);
            }}
          >
            Anular este movimiento
          </Button>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * El pie: una linea por moneda con plata, y la aclaracion de lo que no cuenta.
 *
 * Las dos monedas no se suman ni aparece un equivalente: son dos grupos
 * disjuntos, y las cantidades son lo que deja ver que 12 + 2 = 14.
 */
function PieDelPeriodo({ totales }: { totales: TotalesMovimientos }): JSX.Element {
  return (
    <TableFooter>
      {totales.ars.cantidad > 0 ? (
        <FilaTotal etiqueta="En pesos" moneda="ARS" total={totales.ars} />
      ) : null}
      {totales.usd.cantidad > 0 ? (
        <FilaTotal etiqueta="En dólares" moneda="USD" total={totales.usd} />
      ) : null}
      {totales.transferencias > 0 ? (
        <TableRow>
          <TableCell colSpan={COLUMNAS} className="font-normal text-muted-foreground">
            No cuenta {totales.transferencias}{" "}
            {totales.transferencias === 1 ? "transferencia" : "transferencias"} entre cuentas
            propias: mover plata de un bolsillo a otro no es entrada ni salida.
          </TableCell>
        </TableRow>
      ) : null}
    </TableFooter>
  );
}

function FilaTotal({
  etiqueta,
  moneda,
  total,
}: {
  etiqueta: string;
  moneda: "ARS" | "USD";
  total: TotalMovimientos;
}): JSX.Element {
  return (
    <TableRow>
      <TableCell colSpan={4}>
        {etiqueta}
        <span className="pl-2 font-normal tabular-nums text-muted-foreground">
          {total.cantidad} {total.cantidad === 1 ? "movimiento" : "movimientos"}
        </span>
      </TableCell>
      <TableCell className="text-right font-normal text-muted-foreground">
        entró <Importe monto={aNumero(total.entro)} moneda={moneda} tamano="sm" codigo={false} />
      </TableCell>
      <TableCell className="text-right font-normal text-muted-foreground">
        salió <Importe monto={aNumero(total.salio)} moneda={moneda} tamano="sm" codigo={false} />
      </TableCell>
      <TableCell />
    </TableRow>
  );
}

/** El mismo pie en mobile, donde no hay tabla que lo sostenga. */
function ResumenMobile({ totales }: { totales: TotalesMovimientos }): JSX.Element {
  return (
    <Card as="section" variant="muted" padding="compacto" className="space-y-1 text-sm">
      {(["ars", "usd"] as const).map((clave) => {
        const total = totales[clave];
        if (total.cantidad === 0) return null;
        const moneda = clave === "ars" ? "ARS" : "USD";
        return (
          <p key={clave} className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-muted-foreground">
              {clave === "ars" ? "En pesos" : "En dólares"} · {total.cantidad}
            </span>
            <span className="flex items-center gap-2">
              <Importe monto={aNumero(total.entro)} moneda={moneda} tamano="sm" codigo={false} />
              <span className="text-muted-foreground">/</span>
              <Importe monto={-aNumero(total.salio)} moneda={moneda} tamano="sm" codigo={false} />
            </span>
          </p>
        );
      })}
      {totales.transferencias > 0 ? (
        <p className="text-xs text-muted-foreground">
          No cuenta {totales.transferencias}{" "}
          {totales.transferencias === 1 ? "transferencia" : "transferencias"} entre cuentas propias.
        </p>
      ) : null}
    </Card>
  );
}

/**
 * El signo va adelante del importe y ademas hay una flecha: en una columna de
 * plata, distinguir entradas de salidas por el color solamente es exactamente
 * como se lee mal una caja. El color va en la flecha, nunca en el numero.
 */
function ImporteFirmado({
  movimiento,
  compacto = false,
}: {
  movimiento: MovimientoCaja;
  /** Sin la etiqueta ARS/USD: en la lista del celular el lugar es del detalle. */
  compacto?: boolean;
}): JSX.Element {
  const entra = movimiento.tipo === "INGRESO";
  const monto = aNumero(movimiento.importe);
  const Icono = entra ? ArrowDownLeft : ArrowUpRight;

  return (
    <span className="inline-flex items-center justify-end gap-1.5 whitespace-nowrap">
      <Icono
        className={cn("size-3.5 shrink-0", entra ? "text-success" : "text-destructive")}
        aria-hidden="true"
      />
      <span className="sr-only">{entra ? "Entra" : "Sale"}</span>
      <Importe
        monto={entra ? monto : -monto}
        moneda={movimiento.moneda}
        tamano="sm"
        signoExplicito
        codigo={!compacto}
      />
    </span>
  );
}

function EtiquetaConcepto({ movimiento }: { movimiento: MovimientoCaja }): JSX.Element {
  const texto = ETIQUETA_CONCEPTO[movimiento.concepto] ?? movimiento.concepto;
  // El cambio de divisa se marca distinto: no es plata que entro al negocio, es
  // plata que ya estaba y cambio de moneda.
  const esTraspaso =
    movimiento.concepto === "TRANSFERENCIA" || movimiento.concepto === "CAMBIO_DIVISA";
  // (0155) Lo anulado y su espejo se ven como tales: los dos quedan en la
  // lista (la caja los cuenta y se compensan), pero ya no son un gasto.
  if (movimiento.anula_id != null) {
    return <Badge variant="outline">Anulación · {texto}</Badge>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      <Badge variant={esTraspaso ? "info" : "secondary"}>{texto}</Badge>
      {movimiento.anulado_at ? <Badge variant="outline">Anulado</Badge> : null}
    </span>
  );
}
